import React, { useCallback, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import {
  Backdrop,
  Button,
  CircularProgress,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  selectedLmsFormMode,
  selectedLmsItemId,
  setLmsFormMode,
} from '../../lmsSlice'
import {
  labDeviceModelColumns as columns,
  lmsFormCreateMode,
  lmsFormEditMode,
  lmsFormNoneMode,
  labDeviceModelDepartmentColumn,
  loadingMessage,
} from '../../constants'
import { toast } from 'react-toastify'
import {
  createDeviceModel,
  getAllLabDepartments,
  getDeviceModelById,
  updateDeviceModelById,
} from '../../../../services/LmsService'
import '../../lms.scss'
import * as Yup from 'yup'

type ColumnType = {
  name: string
  label: string
  mandatory: boolean
  dropDown?: any[]
}

const CreateUpdateLabDeviceModel = () => {
  const mode = useAppSelector(selectedLmsFormMode)
  const selectedLMSItemId = useAppSelector(selectedLmsItemId)
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(true)

  async function addDevice(values: any) {
    const response = await createDeviceModel(values)
    if (response?.success) {
      toast?.success(response?.message)
      dispatch(setLmsFormMode(lmsFormNoneMode))
    } else {
      toast?.error(response?.message)
    }
  }

  async function updateDeviceModel(values: any) {
    const response = await updateDeviceModelById(values)
    if (response?.success) {
      toast?.success(response?.message)
      dispatch(setLmsFormMode(lmsFormNoneMode))
    } else {
      toast?.error(response?.message)
    }
  }

  const {
    values,
    setFieldValue,
    setValues,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
  } = useFormik({
    initialValues: {
      id: '',
      name: '',
      equipment_name: '',
      department: '',
      device_type: '',
      vendor: '',
      default_connection_type: '',
      protocol: '',
      mapping_key: '',
    },
    validationSchema: Yup?.object({
      name: Yup?.string()?.required(),
      equipment_name: Yup?.string()?.required(),
      vendor: Yup?.string()?.required(),
      department: Yup?.string()?.required(),
      device_type: Yup?.string()?.required(),
      default_connection_type: Yup?.string()?.required(),
      protocol: Yup?.string()?.required(),
      mapping_key: Yup?.string()?.required(),
    }),
    onSubmit: (values) => {
      if (mode === lmsFormCreateMode) {
        addDevice(values)
      } else {
        updateDeviceModel(values)
      }
    },
  })

  const setDropDown = useCallback(async () => {
    setLoading(true)
    columns?.forEach(async (column: ColumnType) => {
      if (column?.name === labDeviceModelDepartmentColumn) {
        let departments = await getAllLabDepartments()
        if (departments?.success) {
          departments = departments?.data?.department?.map(
            (item: any) => item?.department_name
          )
          column.dropDown = Array?.isArray(departments) ? departments : []
        } else {
          toast?.error(departments?.message)
        }
      }
    })
    setLoading(false)
  }, [])

  const getDeviceModel = useCallback(async () => {
    const response = await getDeviceModelById(selectedLMSItemId)
    if (!response?.success) {
      toast?.error(response?.message)
    } else {
      setValues(response?.data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLMSItemId, mode])

  useEffect(() => {
    if (mode === lmsFormEditMode) {
      getDeviceModel()
    }
  }, [mode, getDeviceModel])

  useEffect(() => {
    setDropDown()
  }, [mode, setDropDown])

  return (
    <>
      {loading && (
        <Backdrop className='backdrop' open={loading}>
          <Typography variant='h6' color='whitesmoke'>
            {loadingMessage}
          </Typography>
          <CircularProgress color='inherit' />
        </Backdrop>
      )}
      <form onSubmit={handleSubmit} className='labDevicesFormStyle'>
        <div className='labDeviceFormFirstDivStyle'>
          {columns?.map((column, index) => (
            <div className='labDeviceFormSecondDivStyle' key={index}>
              <div className='labDeviceFormThirdDivStyle'>
                {mode !== lmsFormNoneMode && (
                  <label
                    id={column?.name}
                    htmlFor={column?.name}
                    className='labDeviceFormLabelStyle fieldSet'
                  >
                    {column?.label}&nbsp;
                    {column?.mandatory && (
                      <span className='required_red'>*</span>
                    )}
                    &nbsp; :
                  </label>
                )}
              </div>
              <div className='labDeviceFormTextFieldDiv'>
                {!column?.dropDown ? (
                  <div className='formValidationErrorStyle'>
                    <TextField
                      className='labDeviceFormTextFieldStyle'
                      id={column?.name}
                      name={column?.name}
                      aria-labelledby={column?.name}
                      value={values[column?.name as keyof typeof values]}
                      onChange={handleChange}
                      error={
                        !!touched[column?.name as keyof typeof touched] &&
                        !!errors[column?.name as keyof typeof errors]
                      }
                      size='small'
                      onBlur={handleBlur}
                    />
                    {touched[column?.name as keyof typeof touched] &&
                      errors[column?.name as keyof typeof touched] && (
                        <Typography variant='caption' color='error'>
                          {errors[column?.name as keyof typeof touched]}
                        </Typography>
                      )}
                  </div>
                ) : (
                  <div className='formValidationErrorStyle'>
                    <Select
                      className='labDeviceFormTextFieldStyle'
                      id={column?.name}
                      name={column?.name}
                      labelId={column?.name}
                      inputProps={{ 'data-testid': `${column?.name}-select` }}
                      value={values[column?.name as keyof typeof values]}
                      onChange={(event) =>
                        setFieldValue(column?.name, event?.target?.value)
                      }
                      input={<OutlinedInput className='selectFieldBorder' />}
                      onBlur={handleBlur}
                      error={
                        !!touched[column?.name as keyof typeof touched] &&
                        !!errors[column?.name as keyof typeof errors]
                      }
                    >
                      {Array?.isArray(column?.dropDown) &&
                        column?.dropDown?.map((option: string) => (
                          <MenuItem key={crypto?.randomUUID()} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                    </Select>
                    {touched?.[column?.name as keyof typeof touched] &&
                      errors?.[column?.name as keyof typeof touched] && (
                        <Typography variant='caption' color='error'>
                          {errors?.[column?.name as keyof typeof touched]}
                        </Typography>
                      )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className='labDeviceFormSubmitDivButtonStyle'>
          <Button
            variant='contained'
            type='submit'
            size='small'
            className='labDeviceFormSubmitButtonStyle'
          >
            Submit
          </Button>
        </div>
      </form>
    </>
  )
}

export default CreateUpdateLabDeviceModel
